import { useEffect, useRef, useState } from "react";
import { Send, Loader2, Plus, Lock, X } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { PinnedMessages } from "../components/PinnedMessages";
import { AttachPicker, PendingFiles, AttachmentList } from "../components/MsgAttachments";
import { MsgAvatar } from "../components/MsgAvatar";

const hhmm = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const B = "/agent-app/messages";

const Tabs = ({ data, ch, setCh, onNew }) => (
  <div className="flex gap-2 mb-3 overflow-x-auto pb-1 -mx-1 px-1" data-testid="agent-msg-tabs">
    <button type="button" data-testid="agent-msg-new-subject" onClick={onNew} className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold border border-dashed border-sky-400 text-sky-700 bg-sky-50"><Plus className="h-3.5 w-3.5" /> Nouveau sujet</button>
    {data.map((c) => (
      <button key={c.key} data-testid={`agent-msg-channel-${c.channel === "responsable" ? "t-" + c.thread_id : c.key}`} onClick={() => setCh(c.key)}
        className={`relative shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold border max-w-[60vw] ${c.key === ch ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
        {c.closed_at && <Lock className="h-3 w-3 shrink-0" />}<span className="truncate">{c.channel === "responsable" ? c.label : `Archives ${c.label}`}</span>
        {c.unread > 0 && <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-rose-600 ring-2 ring-white" />}
      </button>
    ))}
  </div>
);

const Bubble = ({ m }) => {
  const mine = m.author_type === "agent";
  if (m.system) return <div className="text-center text-[11px] text-slate-500 italic py-1">{m.content} · {hhmm(m.created_at)}</div>;
  return (
    <div className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`}>
      {!mine && <MsgAvatar author={m.user_id} name={m.user_name} base={B} size={30} />}
      <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${mine ? "bg-sky-600 text-white rounded-br-md" : "bg-slate-100 text-slate-900 rounded-bl-md"} ${m.pinned_at ? "ring-2 ring-amber-400" : ""}`}>
        {!mine && <div className="text-[10px] font-bold uppercase opacity-70">{m.user_name}</div>}
        {m.content && <div className="text-sm whitespace-pre-wrap">{m.content}</div>}
        <AttachmentList base={B} items={m.attachments} />
        <div className={`text-[10px] mt-0.5 ${mine ? "text-sky-100" : "text-slate-500"}`}>{hhmm(m.created_at)}</div>
      </div>
      {mine && <MsgAvatar author={m.user_id} name={m.user_name} base={B} size={30} />}
    </div>
  );
};

export default function AgentMessages() {
  const [data, setData] = useState(null);
  const [ch, setCh] = useState(null);
  const [text, setText] = useState("");
  const [subject, setSubject] = useState(null);
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const load = () => http.get(B).then((r) => { setData(r.data); setCh((c) => (r.data.some((x) => x.key === c) ? c : r.data[0]?.key)); }).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, []);
  const cur = data?.find((c) => c.key === ch);
  useEffect(() => { if (cur) http.post(`${B}/seen`, null, { params: { channel: cur.channel } }).catch(() => {}); }, [ch, cur?.messages.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [cur?.messages.length, ch]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !files.length) return;
    setBusy(true); setErr("");
    try {
      const body = { channel: "responsable", content: text.trim(), attachments: files.map((f) => f.id), ...(subject !== null ? { new_subject: subject.trim() || "Nouveau sujet" } : { thread_id: cur?.thread_id }) };
      const r = await http.post(B, body);
      setText(""); setFiles([]); setSubject(null); setCh(r.data.thread_id); await load();
    } catch (er) { setErr(formatApiError(er)); } finally { setBusy(false); }
  };
  const writable = subject !== null || (cur && !cur.readonly);
  return (
    <section data-testid="agent-messages">
      <h2 className="font-display text-lg font-extrabold mb-1">Messages</h2>
      <p className="text-xs text-slate-500 mb-3">Écrivez à votre chef de site référent (ou à l'administration s'il n'y en a pas). Un sujet par discussion. Vos messages restent privés.</p>
      {!data ? <div className="py-10 grid place-items-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div> : <Tabs data={data} ch={ch} setCh={(k) => { setCh(k); setSubject(null); }} onNew={() => setSubject("")} />}
      {(cur || subject !== null) && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {subject !== null ? (
            <div className="px-3 py-2 border-b border-slate-200 bg-sky-50 flex items-center gap-2" data-testid="agent-msg-subject-bar">
              <input data-testid="agent-msg-subject" autoFocus maxLength={80} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Sujet (ex. : demande de congé, tenue…)" className="flex-1 border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm bg-white" />
              <button type="button" onClick={() => setSubject(null)} aria-label="Annuler" className="p-1 text-slate-500"><X className="h-4 w-4" /></button>
            </div>
          ) : cur.channel === "responsable" && (
            <div data-testid="agent-msg-contact" className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs text-slate-600">Votre interlocuteur : <b className="text-slate-900">{cur.contact || "Administration"}</b>{cur.contact_site ? ` · chef de site ${cur.contact_site}` : ""}</div>
          )}
          {subject === null && <PinnedMessages items={cur.pinned} base={B} ackUrl={(id) => `${B}/${id}/ack`} canPin={false} onChange={load} />}
          <div className="max-h-[50vh] overflow-auto p-3 space-y-2.5" data-testid="agent-msg-list">
            {subject === null && cur.messages.map((m) => <Bubble key={m.id} m={m} />)}
            {(subject !== null || cur.messages.length === 0) && <div className="text-center text-sm text-slate-400 py-8">{subject !== null ? "Indiquez le sujet puis écrivez votre premier message." : "Aucun message pour le moment."}</div>}
            <div ref={end} />
          </div>
          {writable ? <>
            <PendingFiles files={files} setFiles={setFiles} />
            <form onSubmit={send} className="border-t border-slate-200 p-2 flex gap-2">
              <AttachPicker camera base={B} files={files} setFiles={setFiles} />
              <input data-testid="agent-msg-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Message à votre responsable…" className="flex-1 border border-slate-300 rounded-full px-3 py-2 text-sm" />
              <button data-testid="agent-msg-send" disabled={busy} className="bg-sky-600 text-white rounded-full px-3 disabled:opacity-60"><Send className="h-4 w-4" /></button>
            </form>
          </> : <div data-testid="agent-msg-readonly" className="border-t border-slate-200 p-3 text-xs text-slate-500">{cur.closed_at ? `Discussion clôturée${cur.closed_by ? ` par ${cur.closed_by}` : ""}.` : "Ancienne conversation, en lecture seule."} Pour écrire, ouvrez un « Nouveau sujet ».</div>}
        </div>
      )}
      {err && <div className="mt-2 text-sm text-rose-700">{err}</div>}
    </section>
  );
}
